<template>
  <div class="lpu-address">
    <InfoCard
      v-if="isMobile"
      :data-popup-map="popupMapJson"
      class="cursor-pointer gr-3"
      icon="ui-icon-place-40"
      :title="`${tStatic('Адрес')}:`"
      :subtitle="tDynamic(lpuAddress, 'address')"
      @click="handlePopupMapClick"
    >
      <template
        v-if="lpu.lpumetro_set?.all.length"
        #extra-content
      >
        <v-divider />

        <div class="d-flex flex-column gr-4">
          <div
            v-for="(lpuMetro, index) in lpu.lpumetro_set.all"
            :key="`lpu-metro-${index}`"
            class="d-flex align-start text-body-1 gc-2"
          >
            <span
              class="lpu-address__metro-color flex-shrink-0"
              :style="{ backgroundColor: lpuMetro.metro.color }"
            />
            <span class="text-secondary--text">
              {{ tDynamic(lpuMetro.metro, 'name') }}
            </span>
            <span class="d-inline-flex align-center gc-1 flex-shrink-0 ml-auto">
              <span class="ui-icon-pedestrian icon-secondary--text" />
              <span class="text-secondary--text">
                {{ lpuMetro.walk_time < 60 ? '1' : Math.floor(lpuMetro.walk_time / 60) }} {{ tStatic('мин') }}
              </span>
            </span>
          </div>
        </div>
      </template>
    </InfoCard>
    <InfoCard
      v-else-if="!lpu.lpumetro_set?.all.length"
      :data-popup-map="popupMapJson"
      class="cursor-pointer"
      icon="ui-icon-place-40"
      :title="`${tStatic('Адрес')}:`"
      :subtitle="tDynamic(lpuAddress, 'address')"
      @click="handlePopupMapClick"
    />
    <v-menu
      v-else
      attach=""
      class="lpu-address__metro-menu"
      min-width="400"
      max-width="400"
      nudge-bottom="12"
      z-index="110"
      :allow-overflow="false"
      :close-on-content-click="false"
      :open-on-hover="!isMobile"
      offset-y
      right
    >
      <template #activator="{ attrs, on, value }">
        <InfoCard
          v-bind="attrs"
          class="cursor-pointer"
          icon="ui-icon-place-40"
          :data-popup-map="popupMapJson"
          :title="`${tStatic('Адрес')}:`"
          :subtitle="tDynamic(lpuAddress, 'address')"
          use-hover
          @click="handlePopupMapClick"
          v-on="on"
        >
          <template #subtitle-append-icon>
            <span
              class="icon-secondary--text ui-icon-arrow-down lpu-address__menu-icon"
              :class="{ 'lpu-address__menu-icon--rotate-up': value }"
            />
          </template>
        </InfoCard>
      </template>

      <div
        v-for="(lpuMetro, index) in lpu.lpumetro_set?.all"
        :key="`lpu-metro-${index}`"
        class="d-flex align-start text-body-1 gc-2"
      >
        <span
          class="lpu-address__metro-color flex-shrink-0"
          :style="{ backgroundColor: lpuMetro.metro.color }"
        />
        <span class="text-secondary--text">
          {{ tDynamic(lpuMetro.metro, 'name') }}
        </span>
        <span class="d-inline-flex align-center gc-2 flex-shrink-0 ml-auto">
          <span class="ui-icon-pedestrian icon-secondary--text" />
          <span class="text-secondary--text">
            {{ lpuMetro.walk_time < 60 ? '1' : Math.floor(lpuMetro.walk_time / 60) }} {{ tStatic('мин') }}
          </span>
        </span>
      </div>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import formatPhone from 'src/functions/formatPhone'
import getLpuPhone from 'src/utils/getLpuPhone'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import openPopupMap from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/openPopupMap'
import InfoCard from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/HeroSection/components/InfoCard.vue'

const props = defineProps<{
  lpu: Lpu
}>()

const { context, urlLang }: CommonContextStore = useContextStore()
const { reverseFullL10N } = useURLStore()

const lpuAddress = computed(() => props.lpu.addr[0])

const popupMapJson = computed(() => {
  const lpuPhone = getLpuPhone({
    lpu: props.lpu,
    lpuAddress: lpuAddress.value,
  })
  const popupMapData = [
    {
      coords: [
        lpuAddress.value.lat,
        lpuAddress.value.lon,
      ],
      phone: lpuPhone
        ? formatPhone({
          phone: lpuPhone,
          country: context.request.country,
        })
        : '',
      name: props.lpu.name,
      address: lpuAddress.value.address,
      id: props.lpu.id,
      lpuHref: reverseFullL10N({
        name: 'lpu',
        args: {
          language: urlLang,
          town_translit: props.lpu.town.translit,
          lpu_translit: props.lpu.translit,
        },
      }),
    },
  ]

  return JSON.stringify(popupMapData)
})

function handlePopupMapClick() {
  if (!popupMapJson.value) {
    return
  }

  openPopupMap(JSON.parse(popupMapJson.value))
}

const isMobile = useIsMobile()
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

.lpu-address {
  position: relative;

  &__metro-menu {
    & :deep(.v-menu__content) {
      display: flex;
      flex-direction: column;
      row-gap: 16px;
      box-shadow: 0 2px 24px 0 rgba(9, 42, 72, 0.08),
        0 0 2px 0 rgba(21, 40, 92, 0.06),
        0 1px 2px 0 rgba(5, 23, 40, 0.08);
      background-color: $ui-kit-bg-gray-0;
      border-radius: $border-radius-xl;
      padding: 24px;
    }
  }

  &__menu-icon {
    transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);

    &--rotate-up {
      transform: rotate(180deg);
    }
  }

  &__metro-color {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: $border-radius-circle;
    font-size: 1.25em;
    margin-top: calc(0.5em - 4px);
  }
}
</style>
